/* istanbul ignore file */
import React, { useState, useEffect } from "react";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import { useSelector, useDispatch } from "react-redux";
import configData from "../../config.js";
import useStableCallback from "../../hooks/useStableCallback";
import useRequest from "../../hooks/useRequest";
import {
  USER_TYPES,
  setStoreDetails,
  setDistributionCenters,
  setGlobalLoader,
  showNotifier,
} from "../../store/globalSlice";
import SummaryGraphs from "./SummaryGraphs";
import AdvanceSearch from "./AdvanceSearch";
import DashboardTable from "./DashboardTable";

const DashboardSx = { paddingY: 1 };

export default function Dashboard() {
  const dispatch = useDispatch();
  const { requestMaker } = useRequest();
  const userType = useSelector((store) => store?.global?.user?.type);
  const user = useSelector((store) => store?.global?.user);
  const {
    memberNumber,
    storeName,
    city,
    state,
    statusCode,
    projectTypeId,
    planner,
    mac,
    startDate,
    endDate,
  } = useSelector((store) => store?.global?.dashboardFilters);
  const [psGraph, setPsGraph] = useState([]);
  const [ptGraph, setPtGraph] = useState([]);
  const [pcGraph, setPcGraph] = useState([]);
  const [ptNames, setPtNames] = useState([]);
  const [psNames, setPsNames] = useState([]);
  const [plannerList, setPlannerList] = useState([]);
  const [macList, setMacList] = useState([]);
  const [modelStore, setModelStore] = useState([]);
  const [tableData, setTableData] = useState([]);

  const handleDashboardSearch = useStableCallback(() => {
    const memberNumberParts = memberNumber?.split("-") || [];
    const memberNumberValue = memberNumberParts[0] || null;
    const checkDigitValue =
      memberNumberParts.length > 1 ? memberNumberParts[1] : null;
    const filters = {
      memberNumber: !String(memberNumber).trim().length
        ? null
        : memberNumberValue,
      checkDigit: checkDigitValue,
      storeName: !String(storeName).trim().length ? null : storeName,
      city: !String(city).trim().length ? null : city,
      state: !String(state).trim().length ? null : state,
      statusCode:
        statusCode === psNames[0]?.ConfigKey ? null : statusCode || null,
      projectTypeId: projectTypeId || null,
      assignedPlanner: planner || null,
      assignedMac: mac || null,
      createdDateTime: !!startDate ? new Date(startDate) : null,
      completedDateTime: !!endDate ? new Date(endDate) : null,
      user: userType === USER_TYPES[0] ? userType : user.email,
      isArchived: statusCode === psNames[0]?.ConfigKey ? 1 : null,
    };

    dispatch(setGlobalLoader(true));
    requestMaker({
      method: "post",
      url: configData.AdvanceSearch,
      data: filters,
    })
      .then((res) => {
        const details = typeof res?.data === "object" ? res?.data : [];
        setTableData(details);
      })
      .catch((err) =>
        dispatch(
          showNotifier({
            message: "Something went wrong. Please try again.",
            severity: "error",
          })
        )
      )
      .finally(() => dispatch(setGlobalLoader(false)));
  });

  useEffect(() => {
    dispatch(setStoreDetails());

    requestMaker({
      method: "get",
      url: configData.GetProjectStatusSummary,
    }).then((res) => {
      setPsGraph(res.data[0]);
    });

    requestMaker({ method: "get", url: configData.GetProjectTypeSummary }).then(
      (res) => {
        setPtGraph(res.data[0]);
      }
    );

    requestMaker({ method: "get", url: configData.GetProjectCreatedSummary })
      .then((res) => {
        setPcGraph(res.data[0]);
      })
      .catch((err) => console.log(err));

    requestMaker({ method: "get", url: configData.GetAllMasterData })
      .then((res) => {
        setPtNames(res.data.projectType);
        setPsNames(res.data.projectStatus);
        setPlannerList(res.data.planner);
        setMacList(res.data.Mac);
        setModelStore(res.data.modelstore);
        dispatch(setDistributionCenters(res.data.distributioncenter));
        handleDashboardSearch();
      })
      .catch((err) => console.log(err));
  }, [dispatch, requestMaker]);

  useEffect(() => {
    handleDashboardSearch();
  }, [userType]);

  return (
    <Container maxWidth={false} sx={DashboardSx}>
      <Stack spacing={1}>
        <SummaryGraphs psData={psGraph} ptData={ptGraph} pcData={pcGraph} />
        <AdvanceSearch
          psData={psNames}
          ptData={ptNames}
          plannerData={plannerList}
          macData={macList}
          modelStore={modelStore}
          handleSearch={handleDashboardSearch}
        />
        <DashboardTable data={tableData} />
      </Stack>
    </Container>
  );
}
